import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Visibility, Edit, Delete, Search } from "@mui/icons-material";
import axios from "axios";
import { ENDPOINT, IMAGE_BASE_URL } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const HighLightList = () => {
  const [highlight, setHighLight] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");

  const pageSize = 10;

  // ============================
  // FETCH BANNERS
  // ============================
  const fetchHighLight = async (page = 1, searchQuery = "") => {
    setLoading(true);
    try {
      const url = `${ENDPOINT.HIGHLIGHT_LIST}?page=${page}&search=${encodeURIComponent(
        searchQuery
      )}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data;

      setHighLight(data.results || []);
      setCount(data.count || 0);
      setTotalPages(data.total_pages || 1);
      setCurrentPage(page);
    } catch (err) {
      toast.error("Failed to load highlight");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if(!token){
      navigate("/login")
    }else{
      fetchHighLight(1, "");
    }
  }, []);

  // ============================
  // SEARCH
  // ============================
  const handleSearch = (e) => {
    e.preventDefault();
    fetchHighLight(1, search);
  };

  // ============================
  // PAGINATION
  // ============================
  const handleNext = () => {
    if (currentPage < totalPages) {
      fetchHighLight(currentPage + 1, search);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      fetchHighLight(currentPage - 1, search);
    }
  };

  // ============================
  // DELETE
  // ============================
  const openDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDialog(true);
  };

  const closeDeleteDialog = () => setOpenDialog(false);

  const handleDelete = async () => {
    try {
      await axios.delete(ENDPOINT.HIGHLIGHT_DELETE(deleteId), {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("HighLight deleted!");

      if (highlight.length === 1 && currentPage > 1) {
        fetchHighLight(currentPage - 1, search);
      } else {
        fetchHighLight(currentPage, search);
      }
    } catch (err) {
      toast.error("Delete failed");
    } finally {
      closeDeleteDialog();
    }
  };

  // ============================
  // UI
  // ============================
  return (
    <Container maxWidth="xl" sx={{ mt: 5 }}>
      <ToastContainer />

      {/* SEARCH */}
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Box component="form" onSubmit={handleSearch}>
          <TextField
            size="small"
            placeholder="Search HighLight..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: 280 }}
          />

          <Button
            type="submit"
            variant="contained"
            sx={{ ml: 1 }}
            disabled={loading}
          >
            Search
          </Button>
        </Box>

        <Button
          variant="contained"
          color="success"
          onClick={() => navigate("/highlight/create")}
        >
          + Create HighLight
        </Button>
      </Stack>

      {/* TABLE */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>No</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {highlight.length ? (
                  highlight.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {(currentPage - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell>
                        <Avatar
                          src={`${IMAGE_BASE_URL}${item.image}`}
                          variant="rounded"
                          sx={{ width: 70, height: 70 }}
                        />
                      </TableCell>

                      <TableCell>{item.name}</TableCell>

                      <TableCell>{item.category.name}</TableCell>

                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <Tooltip title="View">
                            <IconButton
                              onClick={() => navigate(`/highlight/${item.id}`)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Update">
                            <IconButton
                              color="warning"
                              onClick={() => navigate(`/highlight/update/${item.id}`)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() => openDeleteDialog(item.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No highlight found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* PAGINATION */}
          <Divider />

          <Stack direction="row" justifyContent="space-between" p={2}>
            <Typography>
              Total: <b>{count}</b> | Pages: <b>{totalPages}</b>
            </Typography>

            <Stack direction="row" spacing={2}>
              <Button disabled={currentPage <= 1} onClick={handlePrevious}>
                ← Previous
              </Button>

              <Typography>
                Page {currentPage} / {totalPages}
              </Typography>

              <Button disabled={currentPage >= totalPages} onClick={handleNext}>
                Next →
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* DELETE DIALOG */}
      <Dialog open={openDialog} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this highlight?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HighLightList;
